import {
  BufferAttribute,
  BufferGeometry,
  Color,
  DoubleSide,
  MeshBasicMaterial,
  RepeatWrapping,
  SRGBColorSpace,
  Texture,
  TextureLoader,
} from 'three'
import { ToonMaterial, type ClipMask } from './toonMaterial'
import { hiddenMaterialSpec } from './palette'
import { decodeMeshBinary, type MeshData } from './meshBinary'

/**
 * Loads the payload produced by `scripts/buildAssets.mjs`.
 *
 * Everything on disk is authored in Unity space, so geometry is converted once
 * here, at load, and never again: negate Z on positions and normals, then
 * reverse triangle winding, because mirroring an axis flips handedness and
 * would otherwise leave every mesh inside-out.
 */

export interface ManifestRGBA {
  r: number
  g: number
  b: number
  a: number
}

/** A texture slot with the tiling and offset the material assigned it. */
export interface ManifestTextureSlot {
  texture: string
  scale: [number, number]
  offset: [number, number]
}

/** A detail map hung off one channel of the vertex-colour mask. */
export interface ManifestDetailSlot extends ManifestTextureSlot {
  brightness: number
  strength: number
  midpoint: number
  triplanar: boolean
}

/**
 * Everything the Stylized Toon shader family reads off a material.
 *
 * Four variants ship in this bundle — `StylizedToon`, `Char`, `Ground` and
 * `ShadowBlob` — and the optional blocks below are what separates them. See
 * `toonMaterial.ts` for what the numbers mean and which parts are recovered
 * versus reconstructed.
 */
export interface ManifestToon {
  /**
   * Which of the four toon variants, `'invisible'` for the stencil-only
   * `RuffGames/invisible` (draws nothing — see `ToonMaterial`), or null if the
   * material uses something else.
   */
  shader: 'StylizedToon' | 'Char' | 'Ground' | 'ShadowBlob' | 'invisible' | null
  highlight: ManifestRGBA
  shadow: ManifestRGBA
  rampThreshold: number
  rampSmoothing: number
  ambientBoost: number
  shadowColorLift: number
  useRamp: boolean
  /** `_RenderFace`: 0 both, 1 front, 2 back. */
  renderFace: number
  /** `_DepthWrite`; off for the buses' soft ground shadow. */
  depthWrite: boolean
  /** `_Surface`: 1 means the material was authored as transparent. */
  transparent: boolean
  baseScale: [number, number]
  baseOffset: [number, number]
  /** `_UseVignette`: only the two ground materials switch this on. */
  vignette?: {
    color: ManifestRGBA
    threshold: number
    smoothing: number
    alpha: number
    rounding: number
  }
  /** `_UseMaskDetail`: the background plate's grain. */
  maskDetail?: ManifestDetailSlot & { centerScale: number }
  /** `_UseMatcap`: a sheen sphere, masked to chosen vertex-colour channels. */
  matcap?: ManifestTextureSlot & {
    color: ManifestRGBA
    strength: number
    contrast: number
    channelMask: number
  }
  /**
   * `_UseDissolve`: the wrapper a hidden bus wears.
   *
   * The texture is `Texture2D/hidden.png` — 256x256 RGBA, and its **alpha is
   * the glyph mask**: 80.5 % of it is transparent and the 18 % that is opaque
   * averages RGB 199.7, dominated by pure white with a black outline. So it is
   * white question marks on nothing, composited over `hidden`; every
   * `Bus_*.mat` and `BusAcc_*.mat` ships the same `hidden` colour, the same
   * 0.38 `textureAlpha` and the same 0.225 tiling.
   */
  dissolve?: ManifestTextureSlot & { hidden: ManifestRGBA; textureAlpha: number }
  /** `_UseRim`: the passengers' darkened silhouette edge. */
  rim?: {
    source: number
    baseShade: number
    min: number
    max: number
    strength: number
  }
}

export interface ManifestMaterial {
  name: string
  color: ManifestRGBA
  emission: ManifestRGBA
  texture: string | null
  toon: ManifestToon
  /**
   * `_DissolveRange` when the material is standing in for a hidden bus.
   *
   * Not read from the `.mat` — every file ships 1, the revealed state. It is
   * set only by `palette.hiddenMaterialSpec`, and its presence is what switches
   * the shader branch on.
   */
  dissolveRange?: number
  /**
   * 📐 Whether the surface takes the sun's shadow map — `_ReceiveShadows: 1`
   * **and** no `_RECEIVE_SHADOWS_OFF` keyword (the keyword is what decides;
   * `Blue.mat` has the float at 1 with the keyword on). Absent on a manifest
   * built before LGT-1, which `ToonMaterial` reads as "does not receive".
   */
  receiveShadows?: boolean
  /**
   * 📐 Whether the material has a `SHADOWCASTER` pass at all — false when the
   * `.mat` lists it under `disabledShaderPasses` (`BusShadows`, `Ice 1`,
   * `BusStationGlass`, …). Absent means it casts.
   */
  castShadows?: boolean
  /**
   * The vertex-colour palette, when the material uses one.
   *
   * The shader blends these three against the mesh's vertex-colour mask; the
   * base colour stays white. This is where a bus's colour actually lives — and
   * the per-channel textures are how the raised plate gets its checkerboard and
   * the lift its hatching without a second material.
   */
  palette?: {
    r: ManifestRGBA | null
    g: ManifestRGBA | null
    b: ManifestRGBA | null
    shadow: ManifestRGBA | null
    rTexture: ManifestDetailSlot | null
    gTexture: ManifestDetailSlot | null
    bTexture: ManifestDetailSlot | null
  }
}

/**
 * A SpriteRenderer on a scene or prefab node.
 *
 * Unity draws a sprite as a window onto an atlas, so the quad's size and its
 * UV rect both come from the sprite asset rather than the texture. The bus
 * glyph on the kerbside sign, the one on the capacity board and the board's
 * drop shadow are all this and nothing else.
 */
export interface ManifestSprite {
  texture: string
  /** The sprite's rect within its texture, as u0, v0, u1, v1. */
  uv: [number, number, number, number]
  /** Quad size in world units, from the rect over m_PixelsToUnits. */
  size: [number, number]
  color: ManifestRGBA
}

export interface ManifestNode {
  name: string
  sprite?: ManifestSprite
  active: boolean
  visible: boolean
  position: { x: number; y: number; z: number }
  rotation: { x: number; y: number; z: number; w: number }
  scale: { x: number; y: number; z: number }
  mesh: string | null
  materials: string[]
  /**
   * 📐 The renderer's `m_CastShadows` is not `Off`. Absent means it casts —
   * the manifest before LGT-1 did not carry it, and `On` is Unity's default.
   * Whether a draw group actually casts also needs its material's pass — see
   * `applyShadowCasting`.
   */
  castShadows?: boolean
  children: ManifestNode[]
}

export interface Manifest {
  meshes: string[]
  /**
   * Texture name -> the file that ships for it, extension included (AST-5).
   *
   * A map rather than a list because the extension is not predictable any more:
   * a texture whose PNG survived the WebP round trip bit for bit ships as
   * `<name>.webp`, and one whose RGB under alpha == 0 did not still ships as
   * `<name>.png`. `scripts/webpCache.json` records which is which and why.
   */
  textures: Record<string, string>
  /**
   * Per-vertex position deltas for meshes that carry a blend shape, flat and
   * Unity-space. `BusCenter` has one — the `Anim` channel that
   * `Bus.PlayAccommodationAnimation` tweens as a bus takes to the road.
   */
  blendShapes: Record<string, number[]>
  materials: Record<string, ManifestMaterial>
  prefabs: Record<string, ManifestNode>
  /**
   * `Gameplay.unity`'s static dressing, as one node tree under a synthetic
   * `root`: the ground plate, the raised platform, the road stubs and the
   * kerbside props, at their authored world transforms. Null if the scene was
   * not in the export.
   */
  scene: ManifestNode | null
  /** Sprite and font files copied into `assets/ui/` for the HUD. */
  ui: string[]
}

export class AssetLibrary {
  private manifest!: Manifest
  private geometries = new Map<string, BufferGeometry>()
  private textures = new Map<string, Texture>()
  /**
   * One promise per requested texture, settled when the image has arrived —
   * or failed. `texture()` hands the caller a `Texture` whose pixels are still
   * in flight, so this is the only way to ask "is it actually there yet".
   */
  private texturesPending = new Map<string, Promise<void>>()
  private materials = new Map<string, ToonMaterial>()
  /** Plain material to its road-cutting twin, indexed by `ClipMask`. See `clipped`. */
  private clipTwins = new Map<ToonMaterial, ToonMaterial[]>()
  /** Each twin back to the plain material it was made from. */
  private plainTwins = new Map<ToonMaterial, ToonMaterial>()
  /** Plain material to its `ApplyHiddenMaterialState` twin. See `hidden`. */
  private hiddenTwins = new Map<ToonMaterial, ToonMaterial>()
  private sprites = new Map<string, MeshBasicMaterial>()

  static async load(baseUrl = 'assets/'): Promise<AssetLibrary> {
    const library = new AssetLibrary()
    library.base = baseUrl
    const res = await fetch(`${baseUrl}manifest.json`)
    if (!res.ok) {
      throw new Error(
        `assets/manifest.json → HTTP ${res.status}. Run \`npm run assets\` to generate it.`,
      )
    }
    library.manifest = (await res.json()) as Manifest
    return library
  }

  private base = 'assets/'

  get prefabs(): Record<string, ManifestNode> {
    return this.manifest.prefabs
  }

  /** The gameplay scene's static dressing, or null. */
  get scene(): ManifestNode | null {
    return this.manifest.scene ?? null
  }

  prefab(name: string): ManifestNode | null {
    return this.manifest.prefabs[name] ?? null
  }

  /** Every mesh a prefab tree references, so they can be prefetched together. */
  meshesUsedBy(node: ManifestNode, into = new Set<string>()): Set<string> {
    if (node.mesh) into.add(node.mesh)
    for (const child of node.children) this.meshesUsedBy(child, into)
    return into
  }

  /**
   * `onProgress` reports finished requests, not bytes: the boot bar is the only
   * caller that wants it (`bootLoader.ts`), and a `.bin` has no length until it
   * has arrived. It fires once with `(0, total)` so a caller can size the bar
   * before the first response.
   */
  async loadMeshes(
    names: Iterable<string>,
    onProgress?: (done: number, total: number) => void,
  ): Promise<void> {
    const list = [...names]
    let done = 0
    onProgress?.(0, list.length)
    await Promise.all(
      list.map(async (n) => {
        await this.mesh(n)
        onProgress?.(++done, list.length)
      }),
    )
  }

  /**
   * Every texture the manifest names, on the GPU's doorstep before the caller
   * moves on.
   *
   * ⚠️ **This exists because `texture()` is fire-and-forget.** It returns a
   * `Texture` with no image and fills it in later, so a scene built and shown
   * the moment `loadMeshes` resolves draws every mesh with an *unbound* map:
   * no albedo, and — the visible part — no `uChannelMap*`, which is where a
   * bus's colour actually lives (see `ToonMaterial`). The first second of the
   * page was the whole level in pale grey, geometry correct and colour absent.
   *
   * 📐 It is worth waiting for: `public/assets/textures` is 5.5 MB against the
   * meshes' 3.3 MB, so the boot was showing a finished bar while the larger
   * half of the payload was still on the wire. `bootLoader.test.ts` weighs the
   * bar's segments against both numbers on disk.
   *
   * Everything the manifest lists is loaded, not just what the first level
   * touches: the set is 34 files after the AST pruning, they are all reachable
   * from gameplay, and a per-level subset would have to be recomputed on every
   * level change — which is exactly where a wash would reappear.
   *
   * `onProgress` counts finished requests, like `loadMeshes`, and fires once
   * with `(0, total)` so a caller can size the bar before the first response.
   */
  async loadTextures(onProgress?: (done: number, total: number) => void): Promise<void> {
    const list = Object.keys(this.manifest.textures)
    let done = 0
    onProgress?.(0, list.length)
    await Promise.all(
      list.map(async (n) => {
        await this.textureReady(n)
        onProgress?.(++done, list.length)
      }),
    )
  }

  /**
   * Resolves once `name` has arrived — or failed, or is not in the manifest.
   * Requests it if nothing has yet.
   */
  async textureReady(name: string): Promise<void> {
    this.texture(name)
    await this.texturesPending.get(name)
  }

  /**
   * Which file actually backs a mesh name.
   *
   * `Bus_Front_0`, `Bus_Front_1` and `Bus_Front` are the per-size variants of
   * one part and are genuinely different geometry. The base name is a fallback
   * for meshes the pipeline could not decode, and **only** when the exact name
   * is absent — resolving to it whenever the exact name misses the cache aliases
   * every variant onto whichever one loaded first. That is what shrank the bus's
   * middle section to the 0.33-unit `BusCenter` slice the seats use.
   */
  private resolveMesh(name: string): string | null {
    if (this.manifest.meshes.includes(name)) return name
    const base = name.replace(/_\d+$/, '')
    return base !== name && this.manifest.meshes.includes(base) ? base : null
  }

  /** Already-loaded geometry, or null. Synchronous, for building prefab trees. */
  peekMesh(name: string): BufferGeometry | null {
    const file = this.resolveMesh(name)
    return file ? (this.geometries.get(file) ?? null) : null
  }

  async mesh(name: string): Promise<BufferGeometry | null> {
    const file = this.resolveMesh(name)
    if (!file) return null
    const cached = this.geometries.get(file)
    if (cached) return cached

    // MeshBinary, written by `buildAssets` beside the OBJ the node tools read.
    // See `meshBinary.ts` for the format and `geometryFromMeshBinary` for
    // why the result is still a non-indexed geometry.
    const bytes = await fetch(`${this.base}meshes/${file}.bin`).then((r) =>
      r.ok ? r.arrayBuffer() : null,
    )
    if (bytes === null) return null

    const geometry = geometryFromMeshBinary(decodeMeshBinary(bytes))
    if (!geometry) return null

    unityToThreeGeometry(geometry)
    ;(geometry as BufferGeometry).computeBoundingBox()
    ;(geometry as BufferGeometry).computeBoundingSphere()
    // Keyed by the file, not the request, so a fallback never gets filed under
    // a variant's name.
    this.geometries.set(file, geometry)
    return geometry
  }

  texture(name: string): Texture | null {
    const file = this.manifest.textures[name]
    if (!file) return null
    let texture = this.textures.get(name)
    if (!texture) {
      // `TextureLoader.load` returns before a single byte has arrived, so the
      // promise is filed here, at the one point where the request is made.
      // Both callbacks settle it: a texture that 404s must not hold the boot
      // sheet up for ever — the material simply draws untextured, which is
      // what it did before `loadTextures` existed.
      let settle!: () => void
      this.texturesPending.set(name, new Promise<void>((resolve) => (settle = resolve)))
      const onError = () => {
        console.warn(`[assets] texture ${name} (${file}) failed to load`)
        settle()
      }
      // The manifest carries the extension (AST-5) — `.webp` where the round
      // trip was bit-exact, `.png` where it was not. Appending `.png` here
      // would 404 half the bundle.
      texture = new TextureLoader().load(`${this.base}textures/${file}`, settle, undefined, onError)
      texture.colorSpace = SRGBColorSpace
      texture.wrapS = RepeatWrapping
      texture.wrapT = RepeatWrapping
      // three flips textures by default, which matches how Unity stores them.
      texture.flipY = true
      this.textures.set(name, texture)
    }
    return texture
  }

  /**
   * The unlit quad a SpriteRenderer draws.
   *
   * Unity puts `Sprite-Unlit-Default` on every one of these, so there is no
   * ramp and no light — just the texture, the tint and its alpha, with depth
   * writes off the way a sprite layer behaves.
   */
  spriteMaterial(sprite: ManifestSprite): MeshBasicMaterial {
    const key = `sprite:${sprite.texture}#${sprite.color.a}`
    const cached = this.sprites.get(key)
    if (cached) return cached

    const material = new MeshBasicMaterial({
      map: this.texture(sprite.texture),
      transparent: true,
      opacity: sprite.color.a,
      depthWrite: false,
      side: DoubleSide,
    })
    material.color.setRGB(sprite.color.r, sprite.color.g, sprite.color.b, 'srgb')
    this.sprites.set(key, material)
    return material
  }

  /** The manifest's record for a material, or null. */
  materialSpec(name: string): ManifestMaterial | null {
    return this.manifest.materials[name] ?? null
  }

  /**
   * A mesh's blend shape deltas, already mirrored into three space.
   *
   * Z is negated to match `unityToThreeGeometry`; X and Y pass through. Comes
   * back flat, three per vertex, indexed the same as the geometry's positions.
   */
  blendShape(name: string): Float32Array | null {
    const file = this.resolveMesh(name)
    const raw = file ? this.manifest.blendShapes?.[file] : null
    if (!raw) return null
    const cached = this.blendShapeCache.get(file!)
    if (cached) return cached
    const out = new Float32Array(raw.length)
    for (let i = 0; i < raw.length; i += 3) {
      out[i] = raw[i]
      out[i + 1] = raw[i + 1]
      out[i + 2] = -raw[i + 2]
    }
    this.blendShapeCache.set(file!, out)
    return out
  }

  private blendShapeCache = new Map<string, Float32Array>()

  /**
   * A material from the manifest, as a port of the shader it was authored for.
   *
   * `tint` overrides the base colour. It is a fallback for anything without a
   * vertex-colour palette — a bus takes its colour from `palette.ts` instead,
   * by swapping to the `Bus_<Colour>` material and resolving the mesh's mask,
   * which is what the original does.
   *
   * Emission is read into the manifest but not applied: nothing the gameplay
   * scene draws sets it, and `Custom_StylizedToon` has no emissive term to
   * carry it.
   */
  material(name: string, tint?: Color): ToonMaterial {
    const key = tint ? `${name}#${tint.getHexString()}` : name
    const cached = this.materials.get(key)
    if (cached) return cached

    // The shader's feature set is baked into its #defines, so it is decided
    // once here from the manifest rather than mutated afterwards.
    const material = new ToonMaterial(this.manifest.materials[name] ?? null, (t) => this.texture(t))
    material.name = name
    if (tint) material.color.copy(tint)

    this.materials.set(key, material)
    return material
  }

  /**
   * The same material cutting against the ends of the drawn road.
   *
   * A bus whose body is laid out along the spline is drawn with one of these
   * instead of its own; everything else in the frame keeps the plain one, has
   * no `aRoadDistance` to read, and stays whole. It has to be a separate
   * material rather than a define toggled around the draw — see `RoadWindow`.
   *
   * There is only ever one twin now (`ROAD_CUT`); the array is kept because the
   * mask used to have four values and nothing is gained by narrowing it.
   *
   * Keyed on the material object, not its name, so a tinted variant gets its
   * own twins and they carry the tint with them.
   */
  clipped(material: ToonMaterial, mask: ClipMask): ToonMaterial {
    if (mask === 0) return material.clip === 0 ? material : this.plainOf(material)
    // Asking a twin for a twin gives the plain material's, so a bus whose mask
    // changes while it is in a mouth swaps straight across.
    const plain = this.plainOf(material)
    const twins = this.clipTwins.get(plain) ?? []
    const cached = twins[mask]
    if (cached) return cached

    const twin = new ToonMaterial(plain.spec, (t) => this.texture(t), mask)
    twin.name = plain.name
    twin.color.copy(plain.color)
    twins[mask] = twin
    this.clipTwins.set(plain, twins)
    this.plainTwins.set(twin, plain)
    return twin
  }

  /** The uncut material a clipping twin was made from, or the material itself. */
  private plainOf(material: ToonMaterial): ToonMaterial {
    return this.plainTwins.get(material) ?? material
  }

  /**
   * The same material with `Bus.ApplyHiddenMaterialState` applied.
   *
   * A **twin**, cached — never a mutation. `material(name)` hands the same
   * object to every bus of that colour, so writing the dissolve onto it would
   * black out the whole fleet.
   *
   * The twin is built from the material's spec through `hiddenMaterialSpec`,
   * which keeps the clip mask, so `clipped(hidden(m), mask)` and
   * `hidden(clipped(m, mask))` both give a hidden material that still cuts at
   * the tunnel mouth — a hidden bus driving into one is not a special case.
   */
  hidden(material: ToonMaterial): ToonMaterial {
    const cached = this.hiddenTwins.get(material)
    if (cached) return cached
    if (!material.spec) return material

    const twin = new ToonMaterial(
      hiddenMaterialSpec(material.spec),
      (t) => this.texture(t),
      material.clip,
    )
    twin.name = material.name
    this.hiddenTwins.set(material, twin)
    // Asking a hidden material for its hidden twin gives itself back, so a
    // repeat call while the bus is still hidden costs nothing.
    this.hiddenTwins.set(twin, twin)
    return twin
  }

  /**
   * A fresh instance of `material` for `Bus.PlayRevealMaterialTween` to write
   * to — the port's `Renderer.materials[0]`.
   *
   * **Not** cached, unlike `hidden` and `clipped`: the reveal writes
   * `_DissolveRange` and `_ShadowColor` onto it every frame for 0.893 s, so two
   * buses revealing at once must not share one (`writeRevealTween` explains
   * why a shared object would reach only one of them). The caller owns it and
   * disposes it when the sequence ends. It starts in the
   * `ApplyHiddenMaterialState` state — range 0, shadow black — which is exactly
   * what `PlayRevealMaterialTween:98-104` writes before inserting the tweens,
   * and it keeps `material.clip`, so a bus revealed at a tunnel mouth is still
   * cut there. Returns null for a material with no spec (nothing to tween).
   */
  revealing(material: ToonMaterial): ToonMaterial | null {
    const plain = this.plainOf(material)
    if (!plain.spec) return null
    const twin = new ToonMaterial(
      hiddenMaterialSpec(plain.spec),
      (t) => this.texture(t),
      material.clip,
    )
    twin.name = material.name
    return twin
  }

  dispose(): void {
    for (const twins of this.clipTwins.values()) for (const m of twins) m?.dispose()
    this.clipTwins.clear()
    this.plainTwins.clear()
    for (const g of this.geometries.values()) g.dispose()
    for (const t of this.textures.values()) t.dispose()
    for (const m of this.materials.values()) m.dispose()
    for (const m of this.sprites.values()) m.dispose()
    this.geometries.clear()
    this.textures.clear()
    this.materials.clear()
    this.sprites.clear()
  }
}

/**
 * The geometry `AssetLibrary.mesh` builds from a decoded MeshBinary — still
 * in Unity space, still to be run through `unityToThreeGeometry`.
 *
 * Attributes go on in file order (`OBJLoader`'s: position, normal, color, uv)
 * and every submesh becomes a draw group, so `materials[i]` still pairs with
 * Unity submesh `i` — each bus part is its colour plus a `BusShadows` layer,
 * and the second was silently dropped once before, when only the first
 * submesh was kept. A single-submesh mesh gets **no** group, exactly as one
 * `OBJLoader` part did when it skipped `mergeGeometries`.
 *
 * Then `toNonIndexed()`. The file is indexed to be small on the wire, but the
 * geometry the rest of the renderer holds stays expanded, one vertex per
 * triangle corner in index order, because that is the shape `OBJLoader`
 * produced and two things are written against it: `manifest.blendShapes` is
 * emitted **in face order** (`decodeMeshAsset` in `buildAssets.mjs`), and
 * `busView`'s bend/ring code reads positions per corner. Switching the live
 * geometry to indexed is a separate change with its own measurements, not a
 * side effect of changing the file format (AST-1 is A/B'd to 0 pixels on the
 * promise that nothing downstream can tell the difference).
 *
 * A mesh with no normal channel (`SM_LightRay_Round`, the headlight shaft)
 * comes back without a `normal` attribute, and `unityToThreeGeometry` computes
 * flat ones after the mirror. `OBJLoader` used to invent them itself on the
 * way in (`addFaceNormal`); the two agree to one f32 ulp, and that mesh is
 * drawn unlit — `tests/meshBinary.test.ts` holds the measurement.
 *
 * Null for an empty index — the OBJ path returned null when no part had faces.
 */
export function geometryFromMeshBinary(data: MeshData): BufferGeometry | null {
  if (data.index.length === 0) return null
  const indexed = new BufferGeometry()
  for (const a of data.attributes) {
    indexed.setAttribute(a.name, new BufferAttribute(a.array, a.itemSize))
  }
  indexed.setIndex(new BufferAttribute(data.index, 1))
  if (data.groups.length > 1) {
    for (const g of data.groups) indexed.addGroup(g.start, g.count, g.materialIndex)
  }
  const geometry = indexed.toNonIndexed()
  indexed.dispose()
  return geometry
}

/**
 * Convert geometry from Unity space in place.
 *
 * Negating Z mirrors the mesh, which reverses triangle winding — without
 * flipping it back, backface culling shows the inside of every model.
 */
export function unityToThreeGeometry(geometry: BufferGeometry): void {
  const position = geometry.getAttribute('position')
  for (let i = 0; i < position.count; i++) {
    position.setZ(i, -position.getZ(i))
  }
  position.needsUpdate = true

  const normal = geometry.getAttribute('normal')
  if (normal) {
    for (let i = 0; i < normal.count; i++) normal.setZ(i, -normal.getZ(i))
    normal.needsUpdate = true
  }

  const index = geometry.getIndex()
  if (index) {
    const array = index.array as Uint16Array | Uint32Array
    for (let i = 0; i < array.length; i += 3) {
      const t = array[i + 1]
      array[i + 1] = array[i + 2]
      array[i + 2] = t
    }
    index.needsUpdate = true
  } else {
    // Non-indexed: swap the 2nd and 3rd vertex of every triangle across all
    // attributes so they stay in step.
    for (const name of Object.keys(geometry.attributes)) {
      const attribute = geometry.getAttribute(name)
      const size = attribute.itemSize
      const array = attribute.array as Float32Array
      for (let tri = 0; tri < attribute.count; tri += 3) {
        const b = (tri + 1) * size
        const c = (tri + 2) * size
        for (let k = 0; k < size; k++) {
          const t = array[b + k]
          array[b + k] = array[c + k]
          array[c + k] = t
        }
      }
      attribute.needsUpdate = true
    }
  }

  if (!normal) geometry.computeVertexNormals()
}
